/**
 * ContactDetailsForm — top section of the Contact Details tab.
 *
 * Fields (Flutter `contact_details_form.dart` parity):
 *   - Ownership pill picker (mandatory; "Self" disabled for minors)
 *   - Email + preferred-star (sets isPreferrable=0 when active)
 *   - Primary contact + preferred-star (sets isPreferrable=1 when active)
 *   - Secondary contact + preferred-star (sets isPreferrable=2 when active)
 *
 * Star icon rules (Flutter `:152`, `:230`, `:317`):
 *   - Filled when isPreferrable matches that slot
 *   - Clicking only takes effect if the corresponding field is non-blank
 *   - Clearing the secondary contact resets isPreferrable to 1 (primary)
 *
 * Validation:
 *   - Email format (only when supplied)
 *   - Secondary contact ≠ primary contact (Flutter inline error
 *     `"Phone number already used as primary contact"`)
 *
 * Phone fields reuse the Signal DS `CountryDialPicker` —
 * same pattern as Demographics so the country picker UX is identical.
 */

import { requiredTone } from '@/patients/components/personal-details/required-tone';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Star } from 'lucide-react';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { CountryDialPicker } from '@scripsteam/scrips-signal-ds';
import { useOwnerTypes } from '@/shared/services/lookups';
import { cn } from '@/lib/utils';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import {
  PREFERRED_EMAIL,
  PREFERRED_PRIMARY,
  PREFERRED_SECONDARY,
  type ContactDetailsFormValues,
} from './contact-details-schema';

export interface ContactDetailsFormProps {
  /** Drives the "Self" ownership entry — disabled for minor patients. */
  isAdult: boolean;
  /** Read-only mode (merged patient) — disables every control. */
  readOnly?: boolean;
}

export function ContactDetailsForm({ isAdult, readOnly }: ContactDetailsFormProps) {
  const { control, setValue, formState } = useFormContext<ContactDetailsFormValues>();
  const isPreferrable = useWatch({ control, name: 'isPreferrable' });
  const email = useWatch({ control, name: 'email' });
  const ownershipValue = useWatch({ control, name: 'ownership' });
  const primary = useWatch({ control, name: 'primaryContact' });
  const secondary = useWatch({ control, name: 'secondayContact' });

  const { data: ownershipList } = useOwnerTypes();

  const setPreferred = (next: 0 | 1 | 2, gateValue: string | null | undefined) => {
    if (readOnly) return;
    if (!gateValue || gateValue.trim().length === 0) return;
    setValue('isPreferrable', next, { shouldDirty: true });
  };

  return (
    <div className="grid gap-6">
      {/* Ownership pill picker */}
      <div>
        <FieldLabel required requiredTone={requiredTone(ownershipValue)}>Ownership</FieldLabel>
        <Controller
          control={control}
          name="ownership"
          render={({ field, fieldState }) => (
            <div className="mt-2">
              <div className="flex flex-wrap gap-2">
                {(ownershipList ?? []).map((entry) => {
                  // Flutter `:64-71`: "Self" disabled for minors.
                  const isSelf = entry.name?.toLowerCase() === 'self';
                  const optionDisabled = readOnly || (isSelf && !isAdult);
                  const selected = field.value === entry.id;
                  return (
                    <button
                      type="button"
                      key={entry.id ?? entry.name ?? ''}
                      disabled={optionDisabled}
                      onClick={() => entry.id && field.onChange(entry.id)}
                      className={cn(
                        'rounded-full px-4 py-1.5 text-sm font-semibold transition-colors',
                        selected
                          ? 'bg-brand-primary text-white'
                          : 'bg-grey-100 text-brand-primary hover:bg-grey-200',
                        optionDisabled && 'cursor-not-allowed opacity-50 hover:bg-grey-100',
                      )}
                    >
                      {entry.name}
                    </button>
                  );
                })}
              </div>
              {fieldState.error?.message ? (
                <p className="mt-1 text-xs text-status-error">
                  {fieldState.error.message}
                </p>
              ) : null}
            </div>
          )}
        />
      </div>

      {/* Email row + preferred star */}
      <div>
        <FieldLabel required requiredTone={requiredTone(email)}>Email address</FieldLabel>
        <div className="mt-1 flex items-center gap-2">
          <div className="flex-1">
            <TextField
              type="email"
              value={email ?? ''}
              onChange={(e) =>
                setValue('email', e.target.value, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              placeholder="Enter email address"
              maxLength={100}
              // Flutter `contact_details_form.dart:107` — email is read-only on
              // the Contact Details tab (edited on Personal Details). Always
              // disabled, regardless of merged/readOnly.
              disabled
              size="compact"
            />
            {formState.errors.email?.message ? (
              <p className="mt-1 text-xs text-status-error">
                {formState.errors.email.message as string}
              </p>
            ) : null}
          </div>
          <PreferredStar
            active={isPreferrable === PREFERRED_EMAIL}
            disabled={readOnly || !email || email.trim().length === 0}
            onToggle={() => setPreferred(PREFERRED_EMAIL, email)}
            aria-label="Set email as preferred contact"
          />
        </div>
      </div>

      {/* Primary phone row + preferred star */}
      <div>
        <FieldLabel required requiredTone={requiredTone(primary)}>Primary contact number</FieldLabel>
        <div className="mt-1 flex items-center gap-2">
          <div className="flex-1">
            <CountryDialPicker
              value={primary ?? ''}
              onChange={(next) =>
                setValue('primaryContact', next, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              // Flutter `contact_details_form.dart:212` — primary contact is
              // read-only on the Contact Details tab (edited on Personal
              // Details). Always disabled.
              disabled
            />
            {formState.errors.primaryContact?.message ? (
              <p className="mt-1 text-xs text-status-error">
                {formState.errors.primaryContact.message as string}
              </p>
            ) : null}
          </div>
          <PreferredStar
            active={isPreferrable === PREFERRED_PRIMARY}
            disabled={readOnly || !primary || primary.trim().length === 0}
            onToggle={() => setPreferred(PREFERRED_PRIMARY, primary)}
            aria-label="Set primary contact as preferred"
          />
        </div>
      </div>

      {/* Secondary phone row + preferred star */}
      <div>
        <FieldLabel>Secondary contact number</FieldLabel>
        <div className="mt-1 flex items-center gap-2">
          <div className="flex-1">
            <CountryDialPicker
              value={secondary ?? ''}
              onChange={(next) => {
                // Flutter `:296-299`: clearing the secondary resets
                // isPreferrable back to 1 (primary).
                const isBlank = !next || next.trim().length === 0;
                setValue('secondayContact', isBlank ? null : next, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                if (isBlank && isPreferrable === PREFERRED_SECONDARY) {
                  setValue('isPreferrable', PREFERRED_PRIMARY, { shouldDirty: true });
                }
              }}
              disabled={readOnly}
            />
            {formState.errors.secondayContact?.message ? (
              <p className="mt-1 text-xs text-status-error">
                {formState.errors.secondayContact.message as string}
              </p>
            ) : null}
          </div>
          <PreferredStar
            active={isPreferrable === PREFERRED_SECONDARY}
            disabled={readOnly || !secondary || secondary.trim().length === 0}
            onToggle={() => setPreferred(PREFERRED_SECONDARY, secondary)}
            aria-label="Set secondary contact as preferred"
          />
        </div>
      </div>
    </div>
  );
}

interface PreferredStarProps {
  active: boolean;
  disabled?: boolean;
  onToggle: () => void;
  'aria-label': string;
}

function PreferredStar({ active, disabled, onToggle, ...rest }: PreferredStarProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      aria-pressed={active}
      aria-label={rest['aria-label']}
      // The app icon button (icon pass, 16 Oct); on = the selected blue (`aria-pressed`) and a filled star.
      className={cn('icon-action', disabled && 'opacity-40 cursor-not-allowed')}
    >
      <Star className="size-4" fill={active ? 'currentColor' : 'none'} aria-hidden />
    </button>
  );
}
